import { useState } from 'react';
import { Badge, Card, ErrorMessage, Field, Loading, ModuleHeader, Tabs, useAsync, useToast } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { COLOR_PROFILES, labelFor, rememberColorProfile } from '../lib/colorProfile';
import { useTheme } from '../lib/theme';
import { MyAbsences } from './MyAbsences';
import { PermissionExplanation } from './admin/PermissionMatrix';
import { IconSettings } from '../components/icons';

export function ProfilePage() {
  const { user, tenant, refreshUser } = useAuth();
  const { preference, setPreference, resolved } = useTheme();
  const { notify } = useToast();
  const [tab, setTab] = useState<'profil' | 'sicherheit' | 'rechte' | 'abwesenheiten'>('profil');

  const permissions = useAsync(() => api.get('/api/auth/me/permissions'), []);
  const sessions = useAsync(() => api.get('/api/auth/me/sessions'), [tab]);

  // `null` = kein eigenes Profil, es gilt das der Organisation.
  const eigenesProfil = user?.color_profile ?? null;
  const waehleProfil = async (wert: string | null) => {
    try {
      await api.patch('/api/auth/me', { colorProfile: wert });
      rememberColorProfile(wert ?? tenant?.colorProfile);
      await refreshUser();
      notify(wert ? `Farbprofil „${labelFor(wert)}“ gespeichert.` : 'Es gilt wieder das Standardprofil der Organisation.', 'success');
    } catch (caught) {
      setError(caught);
    }
  };

  const [profile, setProfile] = useState({
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    phone: user?.phone ?? '',
  });
  const [passwords, setPasswords] = useState({ current: '', next: '', repeat: '' });
  const [error, setError] = useState<unknown>(null);
  const [totpSetup, setTotpSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');

  return (
    <div className="stack measure">
      <ModuleHeader
        icon={<IconSettings width={22} height={22} />}
        title={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`}
        subtitle={
          <>
            {user?.email} · {user?.job_title ?? 'Mitarbeiter'}
            {user?.isSuperadmin ? ' · Superadmin' : ''}
          </>
        }
        pill="Konto"
        tone="primary"
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'profil', label: 'Profil & Darstellung' },
          { key: 'sicherheit', label: 'Sicherheit' },
          { key: 'rechte', label: 'Meine Berechtigungen' },
          { key: 'abwesenheiten', label: 'Abwesenheiten' },
        ]}
      />

      <ErrorMessage error={error} />

      {tab === 'profil' && (
        <div className="stack">
          <Card title="Persönliche Daten">
            <div className="stack">
              <div className="form-grid">
                <Field label="Vorname">
                  <input value={profile.firstName} onChange={(event) => setProfile({ ...profile, firstName: event.target.value })} />
                </Field>
                <Field label="Nachname">
                  <input value={profile.lastName} onChange={(event) => setProfile({ ...profile, lastName: event.target.value })} />
                </Field>
                <Field label="Telefon">
                  <input value={profile.phone} onChange={(event) => setProfile({ ...profile, phone: event.target.value })} />
                </Field>
              </div>
              <div className="row end">
                <button
                  className="btn primary"
                  onClick={async () => {
                    try {
                      await api.patch('/api/auth/me', profile);
                      await refreshUser();
                      notify('Profil gespeichert.', 'success');
                    } catch (caught) {
                      setError(caught);
                    }
                  }}
                >
                  Speichern
                </button>
              </div>
            </div>
          </Card>

          <Card title="Darstellung" subtitle="Hell/Dunkel und Farbprofil gelten nur für Ihr Konto.">
            <div className="stack">
              <div className="btn-group">
                <button className={`btn ${preference === 'light' ? 'active' : ''}`} onClick={() => setPreference('light')}>
                  Hell
                </button>
                <button className={`btn ${preference === 'dark' ? 'active' : ''}`} onClick={() => setPreference('dark')}>
                  Dunkel
                </button>
                <button className={`btn ${preference === 'system' ? 'active' : ''}`} onClick={() => setPreference('system')}>
                  Systemeinstellung
                </button>
              </div>
              <p className="small subtle">
                Aktuell aktiv: <strong>{resolved === 'dark' ? 'Dunkles' : 'Helles'} Design</strong>. Bei
                „Systemeinstellung“ folgt die Oberfläche automatisch den Einstellungen Ihres Betriebssystems.
              </p>

              <div>
                <div className="small muted" style={{ marginBottom: 6 }}>
                  Farbprofil
                </div>
                <div className="farbprofil-wahl">
                  <button
                    type="button"
                    className={`farbprofil-karte${eigenesProfil === null ? ' active' : ''}`}
                    onClick={() => waehleProfil(null)}
                    aria-pressed={eigenesProfil === null}
                  >
                    <span className="farbprofil-name">Standard der Organisation</span>
                    <span className="small subtle">{labelFor(tenant?.colorProfile)}</span>
                  </button>
                  {COLOR_PROFILES.map((profil) => (
                    <button
                      key={profil.key}
                      type="button"
                      className={`farbprofil-karte farbprofil-${profil.key}${eigenesProfil === profil.key ? ' active' : ''}`}
                      onClick={() => waehleProfil(profil.key)}
                      aria-pressed={eigenesProfil === profil.key}
                    >
                      <span className="farbprofil-name">{profil.label}</span>
                      <span className="farbprofil-probe" />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="small muted" style={{ marginBottom: 6 }}>
                  Zugewiesene Rollen
                </div>
                <div className="row" style={{ gap: 6 }}>
                  {(user?.roles ?? []).map((role) => (
                    <Badge key={role.id} variant="primary">
                      {role.name}
                    </Badge>
                  ))}
                  {(user?.roles ?? []).length === 0 && <span className="small subtle">Keine Rollen</span>}
                </div>
              </div>

              <div>
                <div className="small muted" style={{ marginBottom: 6 }}>
                  Gruppen
                </div>
                <div className="row" style={{ gap: 6 }}>
                  {(user?.groups ?? []).map((group) => (
                    <Badge key={group.id} variant={group.inherited ? '' : 'info'}>
                      {group.name}
                      {group.inherited ? ' (geerbt)' : ''}
                    </Badge>
                  ))}
                  {(user?.groups ?? []).length === 0 && <span className="small subtle">Keine Gruppen</span>}
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {tab === 'sicherheit' && (
        <div className="grid zwei-spalten">
          <Card title="Passwort ändern">
            <div className="stack">
              <Field label="Aktuelles Passwort" required>
                <input type="password" value={passwords.current} onChange={(event) => setPasswords({ ...passwords, current: event.target.value })} />
              </Field>
              <Field label="Neues Passwort" required hint="Mindestens 8 Zeichen.">
                <input type="password" value={passwords.next} onChange={(event) => setPasswords({ ...passwords, next: event.target.value })} />
              </Field>
              <Field label="Wiederholung" required error={passwords.repeat && passwords.repeat !== passwords.next ? 'Die Passwörter stimmen nicht überein.' : undefined}>
                <input type="password" value={passwords.repeat} onChange={(event) => setPasswords({ ...passwords, repeat: event.target.value })} />
              </Field>
              <div className="row end">
                <button
                  className="btn primary"
                  disabled={passwords.next.length < 8 || passwords.next !== passwords.repeat}
                  onClick={async () => {
                    try {
                      await api.post('/api/auth/me/password', {
                        currentPassword: passwords.current,
                        newPassword: passwords.next,
                      });
                      setPasswords({ current: '', next: '', repeat: '' });
                      notify('Passwort geändert. Bitte melden Sie sich neu an.', 'success');
                    } catch (caught) {
                      setError(caught);
                    }
                  }}
                >
                  Passwort ändern
                </button>
              </div>
            </div>
          </Card>

          <Card title="Zwei-Faktor-Authentifizierung" subtitle="Zusätzlicher Schutz per Authenticator-App (TOTP)">
            <div className="stack">
              {user?.two_factor_enabled ? (
                <>
                  <div className="alert success">Die Zwei-Faktor-Authentifizierung ist aktiv.</div>
                  {/* Wer die zweite Stufe aufhebt, weist die erste noch einmal
                      nach (Review H6) – sonst hebt jede übernommene Sitzung
                      genau den Schutz auf, der sie verhindern soll. Dasselbe
                      Muster wie bei der Passwortänderung weiter oben. */}
                  <Field label="Aktuelles Passwort" hint="Zum Aufheben der zweiten Stufe erforderlich." required>
                    <input
                      type="password"
                      value={disablePassword}
                      autoComplete="current-password"
                      onChange={(event) => setDisablePassword(event.target.value)}
                    />
                  </Field>
                  <div className="row end">
                    <button
                      className="btn danger"
                      disabled={!disablePassword}
                      onClick={async () => {
                        try {
                          await api.post('/api/auth/me/2fa/disable', { currentPassword: disablePassword });
                          setDisablePassword('');
                          await refreshUser();
                          notify('2FA deaktiviert.', 'success');
                        } catch (caught) {
                          notify(caught instanceof Error ? caught.message : 'Deaktivieren fehlgeschlagen.', 'error');
                        }
                      }}
                    >
                      Deaktivieren
                    </button>
                  </div>
                </>
              ) : totpSetup ? (
                <>
                  <p className="small muted">
                    Fügen Sie folgendes Geheimnis in Ihrer Authenticator-App hinzu und bestätigen Sie mit dem
                    angezeigten Code.
                  </p>
                  <code className="mono" style={{ wordBreak: 'break-all', display: 'block', padding: 10, background: 'var(--surface-2)', borderRadius: 6 }}>
                    {totpSetup.secret}
                  </code>
                  <Field label="Bestätigungscode" required>
                    <input value={totpCode} onChange={(event) => setTotpCode(event.target.value)} maxLength={6} inputMode="numeric" />
                  </Field>
                  <div className="row end">
                    <button
                      className="btn primary"
                      disabled={totpCode.length !== 6}
                      onClick={async () => {
                        try {
                          await api.post('/api/auth/me/2fa/activate', { code: totpCode });
                          setTotpSetup(null);
                          setTotpCode('');
                          await refreshUser();
                          notify('2FA aktiviert.', 'success');
                        } catch (caught) {
                          setError(caught);
                        }
                      }}
                    >
                      Aktivieren
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="small muted">
                    Nach der Aktivierung ist bei jeder Anmeldung zusätzlich ein zeitbasierter Code erforderlich.
                  </p>
                  <div>
                    <button
                      className="btn"
                      onClick={async () => {
                        const setup = await api.post('/api/auth/me/2fa/setup');
                        setTotpSetup(setup);
                      }}
                    >
                      Einrichtung starten
                    </button>
                  </div>
                </>
              )}
            </div>
          </Card>

          <div className="volle-breite">
          <Card title="Aktive Sitzungen" subtitle="Angemeldete Geräte" noBody>
            {sessions.loading ? (
              <Loading />
            ) : (
              <>
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Gerät</th>
                        <th>IP</th>
                        <th>Angemeldet</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sessions.data ?? []).slice(0, 10).map((session: any) => (
                        <tr key={session.id}>
                          <td className="small truncate" style={{ maxWidth: 260 }}>
                            {session.user_agent ?? 'Unbekannt'}
                          </td>
                          <td className="small mono">{session.ip ?? '–'}</td>
                          <td className="small">{formatDateTime(session.created_at)}</td>
                          <td>
                            <Badge variant={session.revoked_at ? '' : 'success'}>
                              {session.revoked_at ? 'Beendet' : 'Aktiv'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="card-body tight row end">
                  <button
                    className="btn sm"
                    onClick={async () => {
                      await api.delete('/api/auth/me/sessions');
                      sessions.reload();
                      notify('Alle Sitzungen wurden beendet.', 'success');
                    }}
                  >
                    Alle Sitzungen beenden
                  </button>
                </div>
              </>
            )}
          </Card>
          </div>
        </div>
      )}

      {tab === 'rechte' && (
        <Card
          title="Meine effektiven Berechtigungen"
          subtitle="Woher jedes Recht stammt: aus einer Rolle, einer Gruppe oder als Einzelrecht"
        >
          {permissions.loading ? (
            <Loading />
          ) : permissions.data?.isSuperadmin ? (
            <div className="alert info">
              Sie sind als <strong>Superadmin</strong> angemeldet und besitzen sämtliche Rechte
              mandantenübergreifend.
            </div>
          ) : (
            <PermissionExplanation
              catalog={permissions.data?.catalog ?? []}
              modules={permissions.data?.modules ?? []}
              effective={permissions.data?.effective ?? {}}
              explanation={permissions.data?.explanation ?? {}}
            />
          )}
        </Card>
      )}

      {tab === 'abwesenheiten' && <MyAbsences />}
    </div>
  );
}
